import { forwardRef, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { mapRiskProfileSocialEconomicResponseToRanking } from './RiskProfileSocialEconomicChartData'
import type {
  RiskProfilePropertiesResponse,
  PropertiesMetricOption,
} from '../types/riskProfileProperties.types'
import { formatRiskProfileValue } from '../utils/riskProfile.utils'

const FALLBACK_DOT_COLOUR = 'hsl(190 54% 51%)'

export interface EconomicAreaScatterChartProps {
  data: RiskProfilePropertiesResponse
  metricOption: PropertiesMetricOption
  entityNoun: string
}

const EconomicAreaScatterChart = forwardRef<ReactECharts, EconomicAreaScatterChartProps>(
  ({ data, metricOption, entityNoun }, ref) => {
    const [dotColour] = useState(() => {
      const resolved = getComputedStyle(document.documentElement)
        .getPropertyValue('--chart-primary')
        .trim()
      return resolved ? `hsl(${resolved})` : FALLBACK_DOT_COLOUR
    })

    const chartData = mapRiskProfileSocialEconomicResponseToRanking(data, {
      metricOption: { ...metricOption, type: 'total' },
      topN: Number.MAX_SAFE_INTEGER,
    })

    if (chartData.labels.length === 0) {
      return (
        <p className="text-sm text-muted-foreground">No comparable areas for this selection.</p>
      )
    }

    const totalOption: PropertiesMetricOption = { ...metricOption, type: 'total' }

    const points = chartData.labels.map((label, index) => {
      const total = chartData.totals[index]
      return [total.value, total.percentValue ?? 0, label]
    })

    const option = {
      tooltip: {
        trigger: 'item',
        formatter: (params: { data: [number, number, string] }) => {
          const [count, percent, label] = params.data
          return `${label}<br/>${formatRiskProfileValue(count, totalOption)} ${entityNoun}<br/>${percent.toLocaleString('en-NZ', { maximumFractionDigits: 1 })}% of area`
        },
      },
      grid: { left: 60, right: 32, top: 24, bottom: 48 },
      xAxis: {
        type: 'value',
        name: `# ${entityNoun} disrupted`,
        nameLocation: 'middle',
        nameGap: 32,
        splitLine: { show: true },
      },
      yAxis: {
        type: 'value',
        name: `% ${entityNoun} disrupted`,
        nameLocation: 'middle',
        nameGap: 40,
        splitLine: { show: true },
      },
      series: [
        {
          type: 'scatter',
          data: points,
          symbolSize: 10,
          itemStyle: { color: dotColour },
        },
      ],
    }

    return <ReactECharts ref={ref} option={option} notMerge style={{ height: 400 }} />
  },
)

EconomicAreaScatterChart.displayName = 'EconomicAreaScatterChart'

export default EconomicAreaScatterChart
